/**
 * The rank-up's text, in the DOM, over the stage's canvas.
 *
 * Everything here is set from the frame the clock hands in — no CSS
 * transitions, no animations, no timers. That is not a style preference: a CSS
 * delay and a JS timer are never the same time, and this screen's whole job is
 * that the word, the pips, the card and the recolour land together.
 *
 * The layout is the kit's grammar (the button, the bar, the pips) on 21 Wild's
 * own screen — the parity kit has no ceremony to copy.
 */

import { lockIcon } from '@/chest/glyphs';
import { tierNumeral, type Rank } from '@/game/xp';
import { rankPalette } from '@/core/tokens';
import {
  escalation,
  LETTER_GAP_MS,
  LETTER_MS,
  RANK_T,
  clamp01,
  easeOutBack,
  type RankFrame,
} from '@/ceremony/timeline';
import type { CeremonyPlan } from '@/ceremony/plan';
import { el, formatScore } from '@/ui/hud';

/** One letter of a slamming word. */
interface Letter {
  readonly node: HTMLElement;
  readonly at: number;
}

function word(text: string, className: string): { node: HTMLElement; letters: Letter[] } {
  const node = el('div', className);
  const letters: Letter[] = [];
  let index = 0;
  for (const ch of text) {
    if (ch === ' ') {
      node.append(el('span', 'w21-cer-space', ' '));
      index++;
      continue;
    }
    const span = el('span', undefined, ch);
    node.append(span);
    letters.push({ node: span, at: index * LETTER_GAP_MS });
    index++;
  }
  return { node, letters };
}

/**
 * The rank's NAME, on its own. The tier is a badge beside it, not more letters of the name: "Wild II" at tier I
 * read as "WILD II I", a typo, and its last "I" slammed in on its own like a ghost glyph.
 */
function rankLabel(rank: Rank): string {
  return rank.name.toUpperCase();
}

/** The label a screen reader hears. */
function rankSpoken(rank: Rank): string {
  return `${rank.name} ${tierNumeral(rank.tier)}`;
}

export interface CeremonyOverlayOptions {
  /** CONTINUE. The ceremony's one button; it opens the chest a rank-up earned. */
  readonly onContinue: () => void;
  /** A tap anywhere else: skip to the end state. */
  readonly onSkip: () => void;
  /** What CONTINUE says — "CONTINUE", or "OPEN CHEST" when one was earned. */
  readonly continueLabel?: string;
  /**
   * A guest's rank is at stake: "Keep your rank", a compact action chip under
   * CONTINUE (never sub-text) that asks to sign up. Absent for an account.
   */
  readonly onKeep?: () => void;
}

export class RankCeremonyOverlay {
  readonly root: HTMLElement;
  #keep: HTMLButtonElement | null = null;
  readonly #plan: CeremonyPlan;
  readonly #old: HTMLElement;
  readonly #kicker: Letter[];
  readonly #kickerNode: HTMLElement;
  readonly #name: Letter[];
  readonly #nameNode: HTMLElement;
  readonly #tier: HTMLElement;
  readonly #plaque: HTMLElement;
  readonly #pips: HTMLElement[] = [];
  readonly #bar: HTMLElement;
  readonly #fill: HTMLElement;
  readonly #barName: HTMLElement;
  readonly #cont: HTMLElement;
  readonly #onKey: (event: KeyboardEvent) => void;
  #lastBarRank = -1;
  #focused = false;

  constructor(plan: CeremonyPlan, opts: CeremonyOverlayOptions) {
    this.#plan = plan;
    this.root = el('div', 'w21-ceremony');
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-label', `Rank up: ${rankSpoken(plan.in)}`);
    this.root.style.setProperty('--rank-old', rankPalette[Math.min(plan.out.index, 8)] ?? '#fff');
    this.root.style.setProperty('--rank-new', rankPalette[Math.min(plan.in.index, 8)] ?? '#fff');
    // The banner plate's hue nudge (degrees): Wild's white-gold sits at a yellow-green hue that a deep plate turns olive; -17 is deep gold.
    this.root.style.setProperty('--plate-hue', plan.in.index >= 8 ? '-17' : '0');
    // Seven's ultramarine plate is the most saturated: a touch darker, so its label (pale, desaturated) holds against it.
    this.root.style.setProperty('--plate-dl', plan.in.index === 1 ? '-0.05' : '0');

    const ui = el('div', 'w21-cer-ui');

    this.#old = el('p', 'w21-cer-old', rankSpoken(plan.out).toUpperCase());
    const kicker = word('RANK UP', 'w21-cer-kicker');
    this.#kicker = kicker.letters;
    this.#kickerNode = kicker.node;
    const label = rankLabel(plan.in);
    const name = word(label, 'w21-cer-name');
    this.#name = name.letters;
    this.#nameNode = name.node;
    // The name's own length, so a long one ("BLACKJACK I") is set to fit the
    // column rather than run off a phone.
    this.#nameNode.style.setProperty('--len', String(Math.max(6, label.length)));
    this.#nameNode.setAttribute('role', 'status');
    // The tier: a small badge that pops beside the name once it has landed.
    this.#tier = el('span', 'w21-cer-tier', tierNumeral(plan.in.tier));
    this.#tier.setAttribute('aria-hidden', 'true');
    const nameRow = el('div', 'w21-cer-namerow');
    nameRow.append(this.#nameNode, this.#tier);
    // The plaque: a band and two hairlines in the rank's colour behind the headline, brighter the higher the rank.
    this.#plaque = el('div', 'w21-cer-plaque');
    this.#plaque.style.setProperty('--esc', escalation(plan.in).toFixed(3));
    this.#plaque.append(this.#kickerNode, nameRow);

    const pips = el('div', 'w21-cer-pips');
    for (let i = 0; i < 5; i++) {
      const pip = el('i', 'w21-cer-pip');
      this.#pips.push(pip);
      pips.append(pip);
    }

    this.#bar = el('div', 'w21-xp-bar w21-md w21-cer-bar');
    this.#bar.setAttribute('role', 'presentation');
    const track = el('div', 'w21-xp-track');
    this.#fill = el('i', 'w21-xp-fill');
    track.append(this.#fill);
    const legend = el('div', 'w21-xp-legend');
    this.#barName = el('span', 'w21-xp-nums');
    legend.append(this.#barName);
    this.#bar.append(track, legend);

    this.#cont = el('button', 'w21-btn w21-primary w21-cer-cont', opts.continueLabel ?? 'Continue');
    (this.#cont as HTMLButtonElement).type = 'button';
    this.#cont.addEventListener('click', (event) => {
      event.stopPropagation();
      opts.onContinue();
    });

    ui.append(this.#old, this.#plaque, pips, this.#bar, this.#cont);
    if (opts.onKeep) {
      const keep = el('button', 'w21-cer-keep') as HTMLButtonElement;
      keep.type = 'button';
      keep.append(lockIcon(), document.createTextNode('Keep your rank'));
      keep.addEventListener('click', (event) => {
        event.stopPropagation();
        opts.onKeep?.();
      });
      this.#keep = keep;
      ui.append(keep);
    }
    this.root.append(ui);
    this.root.addEventListener('click', () => opts.onSkip());
    // Escape skips, like every other overlay in the game; it does not cancel,
    // because there is nothing to cancel — the rank has already been earned.
    this.#onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        opts.onSkip();
      }
    };
    window.addEventListener('keydown', this.#onKey);
  }

  /** Put the stage's canvas behind the text. */
  mountCanvas(canvas: HTMLCanvasElement): void {
    canvas.className = 'w21-cer-canvas';
    this.root.prepend(canvas);
  }

  /**
   * `span` is what the bar reads under it — the count's own XP against the
   * level it is filling toward. It is passed in rather than computed here
   * because the tray and the ceremony must never disagree about the number,
   * and the tray is the one holding the award.
   */
  frame(f: RankFrame, span?: { have: number; ceiling: number }): void {
    this.root.style.setProperty('--veil', String(f.veil));
    this.root.dataset.swapped = String(f.swapped);

    this.#old.style.opacity = String(f.oldName.alpha);
    this.#old.style.transform = `scale(${f.oldName.scale.toFixed(4)})`;

    slam(this.#kicker, f.kickerMs);
    slam(this.#name, f.nameMs);
    const tierU = clamp01((f.t - RANK_T.tier) / 260);
    this.#tier.style.opacity = String(tierU);
    this.#tier.style.transform = `scale(${(0.5 + 0.5 * easeOutBack(tierU, 2.4)).toFixed(4)})`;
    this.#plaque.style.setProperty('--plaque', clamp01(f.kickerMs / 260).toFixed(3));

    for (let i = 0; i < this.#pips.length; i++) {
      const pop = f.pipIn[i] ?? 0;
      const pip = this.#pips[i]!;
      pip.style.opacity = String(pop);
      pip.style.transform = `rotate(45deg) scale(${(0.4 + 0.6 * easeOutBack(pop, 1.6)).toFixed(4)})`;
      const lit = clamp01(f.pipsLit - i);
      pip.dataset.on = lit >= 1 ? 'true' : 'false';
      pip.style.setProperty('--lit', lit.toFixed(3));
    }

    this.#bar.style.opacity = String(f.bar.alpha);
    this.#bar.style.setProperty('--p', f.bar.fill.toFixed(4));
    this.#bar.style.setProperty('--overflow', f.bar.overflow.toFixed(3));
    if (f.bar.rank.index !== this.#lastBarRank) {
      this.#lastBarRank = f.bar.rank.index;
      this.#bar.style.setProperty('--rank', rankPalette[Math.min(f.bar.rank.index, 8)] ?? '#fff');
    }
    if (span) this.setSpan(span.have, span.ceiling);

    // The chip rides CONTINUE's own frame value: it is up when CONTINUE is.
    if (this.#keep) {
      this.#keep.style.opacity = String(f.cont);
      this.#keep.style.transform = `translateY(${((1 - f.cont) * 14).toFixed(2)}px)`;
      this.#keep.style.pointerEvents = f.cont > 0.6 ? 'auto' : 'none';
      this.#keep.tabIndex = f.cont > 0.6 ? 0 : -1;
    }
    this.#cont.style.opacity = String(f.cont);
    this.#cont.style.transform = `translateY(${((1 - f.cont) * 14).toFixed(2)}px)`;
    // Not clickable until it is actually up — an invisible button that
    // answers a tap is how a ceremony gets skipped by accident.
    this.#cont.style.pointerEvents = f.cont > 0.6 ? 'auto' : 'none';
    (this.#cont as HTMLButtonElement).disabled = f.cont <= 0.6;
    // Once it is up, it is where the keyboard is: the ceremony is modal and
    // CONTINUE is the only thing to do.
    if (!this.#focused && f.cont >= 0.999) {
      this.#focused = true;
      this.focusContinue();
    }
  }

  /** The XP the bar is filling toward, for the legend under it. */
  setSpan(have: number, ceiling: number): void {
    this.#barName.textContent = `${formatScore(have)} / ${formatScore(ceiling)} XP`;
  }

  focusContinue(): void {
    (this.#cont as HTMLButtonElement).focus();
  }

  get plan(): CeremonyPlan {
    return this.#plan;
  }

  destroy(): void {
    window.removeEventListener('keydown', this.#onKey);
    this.root.remove();
  }
}

/** A word slamming in letter by letter: each letter's own progress from one time. */
function slam(letters: readonly Letter[], sinceMs: number): void {
  for (const letter of letters) {
    const u = clamp01((sinceMs - letter.at) / LETTER_MS);
    letter.node.style.opacity = String(u);
    const ease = easeOutBack(u, 2.2);
    letter.node.style.transform = `translateY(${((1 - ease) * -14).toFixed(2)}px) scale(${(0.7 + 0.3 * ease).toFixed(4)})`;
  }
}

/** The ceremony's total length, for anything that needs to reserve the time. */
export const CEREMONY_MS = RANK_T.end;
